import { useMemo } from 'react';
import { Plus } from 'lucide-react';
import { mapAudienceSummaryToItem } from '@/components/audiences/map-audience-summary-to-item';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useAudiences } from '@/hooks/use-audiences';
import { useI18nContext } from '@/i18n/i18n-react';
import type { Locales } from '@/i18n/i18n-types';
import { cn } from '@/lib/utils';

const ALL_AUDIENCES_SENTINEL = '__all__' as const;

function toSelectValue(value: string | null) {
  return value ?? ALL_AUDIENCES_SENTINEL;
}

function fromSelectValue(value: string): string | null {
  return value === ALL_AUDIENCES_SENTINEL ? null : value;
}

export interface AudiencePickerProps {
  tenantSlug: string;
  value: string | null;
  onChange: (audienceId: string | null) => void;
  onBlur?: () => void;
  error?: string;
  locale: Locales;
  className?: string;
  disabled?: boolean;
}

export function AudiencePicker({
  tenantSlug,
  value,
  onChange,
  onBlur,
  error,
  locale,
  className,
  disabled,
}: AudiencePickerProps) {
  const { LL } = useI18nContext();
  const { data, isLoading, isError } = useAudiences();

  const audiences = useMemo(
    () => (data ?? []).map(mapAudienceSummaryToItem),
    [data]
  );

  const selectedAudience = value
    ? audiences.find((audience) => audience.id === value)
    : null;

  const entireUserbaseCount = audiences[0]?.totalUserbaseCount ?? 0;

  const estimatedCount = selectedAudience
    ? selectedAudience.targetUsersCount
    : entireUserbaseCount;

  if (isLoading) {
    return (
      <div className={cn('space-y-2', className)}>
        <Label htmlFor="audienceId">
          {LL.createCampaign.sections.audience.label()}
        </Label>
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className={cn('space-y-2', className)}>
        <Label htmlFor="audienceId">
          {LL.createCampaign.sections.audience.label()}
        </Label>
        <p className="text-sm text-red-500">{LL.general.error()}</p>
      </div>
    );
  }

  return (
    <div className={cn('space-y-2', className)}>
      <Label htmlFor="audienceId">
        {LL.createCampaign.sections.audience.label()}
      </Label>
      <div className="flex gap-2">
        <Select
          value={toSelectValue(value)}
          onValueChange={(nextValue) => onChange(fromSelectValue(nextValue))}
          onOpenChange={(open) => {
            if (!open) {
              onBlur?.();
            }
          }}
          disabled={disabled}
        >
          <SelectTrigger
            id="audienceId"
            className={cn('flex-1', error && 'border-red-500')}
          >
            <SelectValue
              placeholder={LL.createCampaign.sections.audience.placeholder()}
            >
              {selectedAudience
                ? selectedAudience.name
                : LL.createCampaign.sections.audience.entireUserbase()}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_AUDIENCES_SENTINEL}>
              {LL.createCampaign.sections.audience.entireUserbase()}
            </SelectItem>
            {audiences.map((audience) => (
              <SelectItem key={audience.id} value={audience.id}>
                {audience.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" className="shrink-0" asChild>
          <a
            href={`/${tenantSlug}/audiences/create`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Plus data-icon="inline-start" />
            {LL.createCampaign.sections.audience.createNew()}
          </a>
        </Button>
      </div>
      <p className="text-sm text-muted-foreground">
        {LL.createCampaign.sections.audience.estimatedSize({
          count: new Intl.NumberFormat(locale).format(estimatedCount),
        })}
      </p>
      {error ? <p className="text-sm text-red-500">{error}</p> : null}
    </div>
  );
}
